/**
* Library to launch notifications on screen.
*
* @class flexygo.msg
*/

namespace flexygo.msg {

    /**
    * Launch an alert dialog. Stops execution until user accepts the msg.
    * @method alert
    * @param {string} str - The message to show.
    */
    export function alert(str: string, callback: LobiBoxConfirmCallback = (() => { }), customTitle?: string): Promise<boolean | void> {
        return new Promise(async (resolve, _) => {
            customTitle = flexygo.localization.translate(customTitle);
            Lobibox.alert("info",
                {
                    title: customTitle,
                    msg: flexygo.localization.translate(str),
                    iconSource: 'fontAwesome',
                    sound: (!flexygo.utils.testMode),
                    soundPath: flexygo.utils.resolveUrl('~/js/plugins/lobibox-master/sounds/'),
                    draggable: true,
                    callback: (dlg: any, type: string, ev: any): void => {
                        if (type == "ok") {
                            callback(true);
                            resolve(true);
                        } else {
                            callback(false);
                            resolve(false);
                        }
                    },
                    closed: (): void => {
                        resolve();
                    }
                }
            );
        })
        
    }



    export interface LobiBoxConfirmCallback { (result: boolean): void }
    export interface LobiBoxQuestionCallback { (result: "yes" | "no"): void }
    /**
    * Launch confirm dialog. Stops execution until user accepts or cancel the dialog.
    * @method confirm
    * @param {string} msg - The message to show.
    * @param {LobiBoxConfirmCallback} callback - The callback function.
    */
    export function confirm(msg: string, callback: LobiBoxConfirmCallback = (() => {})): Promise<boolean|void> {
        return new Promise(async (resolve, _) => {
            let textYes = flexygo.localization.translate('flxedit.areyousuremsgyes');
            textYes = textYes == "flxedit.areyousuremsgyes" ? "Yes" : textYes;
            let textNo = flexygo.localization.translate('flxedit.areyousuremsgno');
            textNo = textNo == "flxedit.areyousuremsgno" ? "No" : textNo;
            let title = flexygo.localization.translate('msg.confirm');
            title = title == "msg.confirm" ? "Please Confirm" : title;
            Lobibox.confirm({
                title: title,
                msg: flexygo.localization.translate(msg),
                iconClass: 'fa fa-question-circle',
                buttons: {
                    yes: {
                        'class': 'lobibox-btn lobibox-btn-yes',
                        text: textYes,
                        closeOnClick: true
                    },
                    no: {
                        'class': 'lobibox-btn lobibox-btn-no',
                        text: textNo,
                        closeOnClick: true
                    }
                },
                draggable: true,
                callback: (dlg: any, type: string, ev: any): void => {
                    if (type == "yes") {
                        callback(true);
                        resolve(true);
                    } else {
                        callback(false);
                        resolve(false);
                    }
                },
                closed: ():void => {
                    resolve();
                }
            });
        });
    }

    /**
   * Launch question dialog. Stops execution until user accepts or cancel the dialog.
   * @method question
   * @param {string} title - The title to show.
   * @param {string} msg - The message to show.
   * @param {LobiBoxQuestionCallback} callback - The callback function.
   */
    export function question(title:string, msg: string, callback: LobiBoxQuestionCallback): void {
        Lobibox.confirm({
            title: title,
            msg: msg,
            iconClass: 'fa fa-question-circle',
            buttons: {
                yes: {
                    'class': 'lobibox-btn lobibox-btn-yes',
                    text: 'Yes',
                    closeOnClick: true
                },
                no: {
                    'class': 'lobibox-btn lobibox-btn-no',
                    text: 'No',
                    closeOnClick: true
                }
            },
            draggable: true,
            callback: (dlg: any, type: string, ev: any): void => {
                if (type == "yes") {
                    callback("yes");
                } else {
                    callback("no");
                }
            }
        });
    }

    /**
    * Displays generic notification.
    * @method generic
    * @param {string} str - The message to show.
    * @param {JQuery} element - Related notification element. If null, displays generic notification.
    * @param {string} customTitle - Notification title.
    * @param {string} [position] - Dialog position.
    * @param {('error'|'info'|'success'|'warning')} type - Css styles type
    */
    export function generic(str: string, element?: JQuery, customTitle?: string, position?: string, type?: string) {

        str = flexygo.localization.translate(str)
        customTitle = flexygo.localization.translate(customTitle);

        if (element) {
            if (!position) { position = "right middle"; }
            $(element).notify(str, { className: type, position: position, clickToHide: true, autoHide: false });
        }
        else {
            let opts = { msg: str, iconSource: 'fontAwesome', title: customTitle, position: position, sound: (!flexygo.utils.testMode), soundPath: flexygo.utils.resolveUrl('~/js/plugins/lobibox-master/sounds/') };
            if (!position) {
                position = "bottom right";
                opts.position = position;
                opts['showClass'] = "fadeInRight";
                opts['hideClass'] = "fadeOutRight";
            }
            Lobibox.notify(type, opts);
        }

    }

    /**
    * Displays error notification.
    * @method error
    * @param {string} str - The message to show.
    * @param {JQuery} element - Related notification element. If null, displays generic notification.
    * @param {string} customTitle - Notification title.
    * @param {string} [position] - Dialog position.
    */
    export function error(str: string, element?: JQuery, customTitle?: string, position?: string) {
        flexygo.msg.generic(str, element, customTitle, position, 'error');
    }

    /**
    * Displays info notification.
    * @method info
    * @param {string} str - The message to show.
    * @param {JQuery} element - Related notification element. If null, displays generic notification.
    * @param {string} customTitle - Notification title.
    * @param {string} [position] - Dialog position.
    */
    export function info(str: string, element?: JQuery, customTitle?: string, position?: string) {
        flexygo.msg.generic(str, element, customTitle, position, 'info');
    }

    /**
    * Displays success notification.
    * @method success
    * @param {string} str - The message to show.
    * @param {JQuery} element - Related notification element. If null, displays generic notification.
    * @param {string} customTitle - Notification title.
    * @param {string} [position] - Dialog position.
    */
    export function success(str: string, element?: JQuery, customTitle?: string, position?: string) {
        flexygo.msg.generic(str, element, customTitle, position, 'success');
    }

    /**
    * Displays warning notification.
    * @method warning
    * @param {string} str - The message to show.
    * @param {JQuery} element - Related notification element. If null, displays generic notification.
    * @param {string} customTitle - Notification title.
    * @param {string} [position] - Dialog position.
    */
    export function warning(str: string, element?: JQuery, customTitle?: string, position?: string) {
        flexygo.msg.generic(str, element, customTitle, position, 'warning');
    }


    /**
    * Launch prompt dialog. Don't block process execution.
    * @method prompt
    * @param {string} title - Dialog title
    * @param {string} msg - The message to show.
    * @param {string} cllback - Callback function after dialog execution (only if Ok button clicked). Recives one string parameter with user input.
    * @param {string} [placeholder] - Input field placeholder text.
    * @param {string} [lines=1] - number of lines for multiline items. 
    * @param {('error'|'info'|'success'|'warning')} [type=text] - Css styles type
    */
    export function prompt(title: string, msg: string, cllback: any, placeholder: string, value: string, lines=1, type="text") {


        if (title) { title = flexygo.localization.translate(title); }
        if (placeholder) { placeholder = flexygo.localization.translate(placeholder); }
        if (msg) { msg = flexygo.localization.translate(msg); }

        var multiline = false;
        if (lines && lines > 1) {
            multiline = true;
        } else {
            lines = 1;
        }

        Lobibox.prompt(type, //Any input type will be valid
            {
                title: title,
                attrs: {
                    placeholder: placeholder
                },
                value: value,          // Value which is given to textfield when messagebox is created
                multiline: multiline,   // Set this true for multiline prompt
                lines: lines,           // This works only for multiline prompt. Number of lines
                type: type,       // Prompt type. Available types (text|number|color)
                label: msg,           // Set some text which will be shown exactly on top of textfield
                required: false,
                draggable: true,
                errorMessage: flexygo.localization.translate(flexygo.localization.translate('msg.fieldrequired')),
                callback: function (lobibox, type) {
                    if (type === 'ok') {
                        cllback(lobibox.getValue());
                    }
                }
            });
    }
}